import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
import calendarApi from '../../services/calendarApi';
import { EventType } from '../../types/eventTypes';
import VietnameseDatePicker from './VietnameseDatePicker';

interface EventDatePickerProps {
    value: Date;
    onChange: (date: Date) => void;
    eventType: EventType;
}

interface LunarInfo {
    day: number;
    month: number;
    year: number;
    isLeapMonth: boolean;
}

const EventDatePicker: React.FC<EventDatePickerProps> = ({ value, onChange, eventType }) => {
    const [lunarInfo, setLunarInfo] = useState<LunarInfo | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isPickerOpen, setIsPickerOpen] = useState(false);

    // Get label based on event type
    const getLabel = () => {
        switch (eventType) {
            case EventType.GIO:
                return 'Ngày Giỗ';
            case EventType.SINH_NHAT:
                return 'Ngày sinh';
            case EventType.SU_KIEN:
                return 'Ngày sự kiện';
            case EventType.MA_CHAY:
                return 'Ngày lễ';
            default:
                return 'Ngày sự kiện';
        }
    };

    // Fetch lunar date when value changes
    useEffect(() => {
        const fetchLunar = async () => {
            setIsLoading(true);
            try {
                const dateStr = format(value, 'yyyy-MM-dd');
                const response = await calendarApi.getDate(dateStr);
                if (response.lunar) {
                    setLunarInfo(response.lunar);
                }
            } catch (error) {
                console.error('Failed to fetch lunar date:', error);
                setLunarInfo(null);
            } finally {
                setIsLoading(false);
            }
        };
        fetchLunar();
    }, [value]);

    // Format lunar date display
    const formatLunarDate = () => {
        if (!lunarInfo) return '';
        const lunarDay = lunarInfo.day;
        const lunarMonth = lunarInfo.month;

        // Vietnamese lunar date format
        if (lunarDay === 1) return `Mồng 1 tháng ${lunarMonth}`;
        if (lunarDay <= 10) return `Mồng ${lunarDay} tháng ${lunarMonth}`;
        return `${lunarDay} tháng ${lunarMonth}`;
    };

    return (
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
                {getLabel()}
            </label>

            {/* Date display card */}
            <div className="bg-white border border-gray-300 rounded-lg p-3 shadow-sm">
                <div className="flex items-center justify-between">
                    <div className="flex-1">
                        {/* Vietnamese formatted date */}
                        <div className="text-base font-medium text-gray-900">
                            {format(value, 'EEEE, dd/MM/yyyy', { locale: vi })}
                        </div>
                        {/* Lunar date */}
                        <div className="text-sm text-amber-600 mt-0.5">
                            {isLoading ? (
                                <span className="text-gray-400 animate-pulse">Đang tải...</span>
                            ) : lunarInfo && (
                                <span>🌙 Âm lịch: {formatLunarDate()}</span>
                            )}
                        </div>
                    </div>

                    {/* Button to trigger date picker */}
                    <button
                        type="button"
                        onClick={() => setIsPickerOpen(true)}
                        className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg text-sm font-medium transition-colors"
                    >
                        Đổi ngày
                    </button>
                </div>
            </div>

            {/* Vietnamese Date Picker Modal */}
            <VietnameseDatePicker
                isOpen={isPickerOpen}
                onClose={() => setIsPickerOpen(false)}
                value={value}
                onChange={onChange}
                title={getLabel()}
            />
        </div>
    );
};

export default EventDatePicker;
